import { ProtectedImg, ProtectedVideo } from "../components/ProtectedMedia";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { api, abs, money } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { AudioPlayer } from "../components/AudioPlayer";

const CATS = [
  ["physical_art", "Original Artworks", "Charcoal on paper, signed and shipped from Sabah", "hornbill-dream"],
  ["digital_art", "Digital Prints", "High-resolution files, delivered instantly", "coral-dreams-of-sipadan"],
  ["audio", "Soundscapes", "Rainforest audio to stream and keep", "rain-on-the-sape"],
  ["video", "Art Films", "Moving charcoal meditations", "dawn-over-the-canopy"],
];

function Hero({ heroUrl, latest, audio }) {
  return (
    <section className="wb-container grid gap-6 pt-8 pb-20 lg:grid-cols-12 lg:gap-8">
      <div className="relative lg:col-span-8 art-frame min-h-[460px] lg:min-h-[640px] fade-up">
        {heroUrl && <ProtectedImg src={abs(heroUrl)} alt="Warna Borneo hero artwork" data-testid="home-hero-image" className="absolute inset-0 h-full w-full object-cover scale-[1.08]" />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(8,9,10,.95) 0%, rgba(8,9,10,.7) 45%, rgba(8,9,10,.15) 80%)" }} />
        <div className="absolute bottom-0 left-0 p-8 md:p-12 max-w-2xl">
          <div className="eyebrow">Ahmad Leong · Sabah, Borneo</div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#F4F4F6]" data-testid="hero-title">
            Charcoal dreams from the <em className="text-[#D4AF37]">heart of Borneo</em>
          </h1>
          <p className="mt-5 max-w-md text-sm md:text-base leading-relaxed text-[#C9CBD1]">
            Surreal nature works drawn by hand — originals, digital prints, soundscapes and art films.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop" className="btn-gold" data-testid="hero-shop-button">Enter the shop <ArrowRight size={14} /></Link>
            <Link to="/gallery" className="btn-ghost !text-[#F4F4F6] !border-white/30 hover:!border-[#D4AF37] hover:!text-[#D4AF37]" data-testid="hero-gallery-button">View gallery</Link>
          </div>
        </div>
      </div>
      <div className="lg:col-span-4 grid gap-6 lg:gap-8">
        {latest && (
          <Link to={`/product/${latest.slug}`} className="group wb-card p-5 fade-up" style={{ animationDelay: "120ms" }} data-testid="hero-latest-drop">
            <div className="eyebrow mb-4">Latest original</div>
            <div className="art-frame aspect-[4/3]"><ProtectedImg src={abs(latest.image_urls[0])} alt={latest.title} className="h-full w-full object-cover" /></div>
            <div className="mt-4 flex items-end justify-between">
              <div className="font-display text-2xl group-hover:text-wb-gold transition-colors">{latest.title}</div>
              <span className="price-tag">{money(latest.price)}</span>
            </div>
          </Link>
        )}
        {audio && (
          <div className="wb-card p-5 fade-up" style={{ animationDelay: "220ms" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="eyebrow">Listen · {audio.title}</div>
              <Link to={`/product/${audio.slug}`} className="text-xs muted hover:text-wb-gold" data-testid="hero-audio-link">{money(audio.price)} →</Link>
            </div>
            <AudioPlayer src={abs(audio.preview_url)} testid="home-audio-preview" />
          </div>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [site, setSite] = useState(null);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => toast.error("Could not load works. Please refresh."));
    api.get("/site").then((r) => setSite(r.data)).catch(() => setSite({}));
  }, []);
  const by = (slug) => products.find((p) => p.slug === slug);
  const featured = products.filter((p) => p.featured).slice(0, 8);
  const heroUrl = site && (site.hero_image_url || by("kinabalu-reverie")?.image_urls[0] || products[0]?.image_urls[0]);
  const latest = products.find((p) => p.type === "physical_art" && !p.sold_out);
  const video = products.find((p) => p.type === "video");

  return (
    <div data-testid="home-page">
      <Hero heroUrl={heroUrl} latest={latest} audio={by("rain-on-the-sape")} />

      <section className="wb-container grid gap-10 py-20 md:grid-cols-12">
        <div className="md:col-span-4"><div className="eyebrow">The artist</div></div>
        <blockquote className="md:col-span-8 font-display text-3xl md:text-4xl leading-snug tracking-tight">
          “I draw the forest the way it feels at dusk — half memory, half dream. Charcoal lets the mist stay in the paper.”
          <footer className="mt-6 font-mono text-xs uppercase tracking-[0.25em] muted">— Ahmad Leong, Kota Kinabalu</footer>
        </blockquote>
      </section>

      <section className="wb-container py-16">
        <div className="mb-12 flex items-end justify-between gap-6">
          <div>
            <div className="eyebrow">Featured works</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">From the studio</h2>
          </div>
          <Link to="/shop" className="text-xs uppercase tracking-[0.2em] muted hover:text-wb-gold transition-colors" data-testid="featured-view-all-link">View all →</Link>
        </div>
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-6">
          {featured.map((p, i) => (
            <div key={p.id} className={i < 2 ? "lg:col-span-3" : "lg:col-span-2"}>
              <ProductCard p={p} index={i} aspect={i < 2 ? "aspect-[5/4]" : "aspect-[4/5]"} />
            </div>
          ))}
        </div>
      </section>

      <section className="wb-container py-20">
        <div className="eyebrow mb-10">Collect by medium</div>
        <div className="grid gap-px sm:grid-cols-2 lg:grid-cols-4" style={{ background: "var(--wb-line)" }}>
          {CATS.map(([type, title, desc, slug]) => {
            const p = by(slug);
            return (
              <Link key={type} to={type === "video" ? "/videos" : `/shop?type=${type}`} data-testid={`category-${type}-tile`} className="group relative block min-h-[340px] overflow-hidden bg-wb-bg">
                {p && <ProtectedImg src={abs(p.image_urls[0])} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-all duration-700 group-hover:opacity-70 group-hover:scale-105" />}
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(8,9,10,.95), transparent 70%)" }} />
                <div className="absolute bottom-0 p-7">
                  <div className="font-mono text-xs text-[#D4AF37]">{String(products.filter((x) => x.type === type).length).padStart(2, "0")}</div>
                  <div className="mt-2 font-display text-3xl text-[#F4F4F6]">{title}</div>
                  <div className="mt-2 text-xs text-[#A1A5B0]">{desc}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {video && (
        <section className="wb-container grid items-center gap-12 py-20 lg:grid-cols-12">
          <div className="lg:col-span-7 relative">
            <ProtectedVideo mp4={abs(video.preview_url)} webm={abs(video.preview_webm_url)} poster={abs(video.image_urls[0])} muted loop autoPlay playsInline className="w-full aspect-video object-cover" data-testid="home-video-teaser" />
          </div>
          <div className="lg:col-span-5">
            <div className="eyebrow">Art film · 30-sec preview</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight">{video.title}</h2>
            <p className="mt-4 text-sm leading-relaxed muted">{video.description}</p>
            <Link to="/videos" className="btn-ghost mt-8" data-testid="home-videos-button"><Play size={14} /> Watch the films</Link>
          </div>
        </section>
      )}
    </div>
  );
}
